import { useCallback, useEffect, useRef, useState } from 'react';
import { useMediaQuery } from '@mui/material';
import { ArrowBackIosNew, ArrowForwardIos } from '@mui/icons-material';
import {
  CardImage,
  CarouselCard,
  CarouselShell,
  CarouselViewport,
  Counter,
  Dot,
  Dots,
  NavButton,
} from './carousel.styled';

interface CarouselProps {
  items: string[];
  /** Accessible label for slide `index` (0-based). */
  label?: (index: number) => string;
  /** Card aspect ratio, width / height. Match the artwork to avoid cropping. */
  ratio?: string;
  /** Largest the card may grow on wide screens. */
  maxWidth?: string;
  /** Auto-advance delay in ms. 0 turns autoplay off. */
  interval?: number;
}

/** How far a finger must travel before it counts as a swipe rather than a tap. */
const SWIPE_THRESHOLD = 45;

/**
 * Stacked image carousel: one slide at a time with its neighbours peeking in
 * behind it. Everything lives inside a single `overflow: hidden` frame, so no
 * matter how narrow the screen gets it can never add a horizontal scrollbar or
 * widen the page — which is what a scrolling strip does on a phone.
 */
const Carousel = ({
  items,
  label = (i) => `Slide ${i + 1}`,
  ratio = '4 / 5',
  maxWidth = '34rem',
  interval = 5000,
}: CarouselProps) => {
  const count = items.length;
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  const go = useCallback(
    (step: number) => setActive((i) => (i + step + count) % count),
    [count]
  );

  // Auto-advance, unless the visitor is interacting or has asked for less motion.
  useEffect(() => {
    if (count < 2 || paused || !interval || prefersReducedMotion) return;
    const timer = setInterval(() => setActive((i) => (i + 1) % count), interval);
    return () => clearInterval(timer);
  }, [count, paused, interval, prefersReducedMotion]);

  /* --- swipe -------------------------------------------------------------
     Tracked by hand rather than with a scroll container: a scroller is what
     produces the horizontal scrollbar we are trying to get rid of. */
  const startX = useRef<number | null>(null);

  const onPointerDown = (event: React.PointerEvent) => {
    startX.current = event.clientX;
    setPaused(true);
  };

  const onPointerUp = (event: React.PointerEvent) => {
    if (startX.current === null) return;
    const dx = event.clientX - startX.current;
    startX.current = null;
    setPaused(false);
    if (Math.abs(dx) > SWIPE_THRESHOLD) go(dx < 0 ? 1 : -1);
  };

  if (count === 0) return null;

  return (
    <CarouselShell
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <CarouselViewport
        $ratio={ratio}
        $maxWidth={maxWidth}
        role="group"
        aria-roledescription="carousel"
        aria-label="Before and after photos"
        tabIndex={0}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerCancel={() => {
          startX.current = null;
          setPaused(false);
        }}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            go(-1);
          }
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            go(1);
          }
        }}
      >
        {items.map((src, i) => {
          // Shortest circular distance, so the stack wraps without a jump.
          let offset = (i - active + count) % count;
          if (offset > count / 2) offset -= count;

          return (
            <CarouselCard key={src + i} $offset={offset} $active={offset === 0}>
              <CardImage
                src={src}
                alt={offset === 0 ? label(i) : ''}
                aria-hidden={offset !== 0}
                loading={i === 0 ? 'eager' : 'lazy'}
                draggable={false}
              />
            </CarouselCard>
          );
        })}

        {count > 1 && (
          <>
            <NavButton
              $side="left"
              type="button"
              onClick={() => go(-1)}
              aria-label="Previous photo"
            >
              <ArrowBackIosNew sx={{ fontSize: 14 }} />
            </NavButton>
            <NavButton
              $side="right"
              type="button"
              onClick={() => go(1)}
              aria-label="Next photo"
            >
              <ArrowForwardIos sx={{ fontSize: 14 }} />
            </NavButton>
            <Counter aria-hidden="true">
              {active + 1} / {count}
            </Counter>
          </>
        )}
      </CarouselViewport>

      {count > 1 && (
        <Dots>
          {items.map((src, i) => (
            <Dot
              key={`dot-${src}-${i}`}
              type="button"
              $active={i === active}
              onClick={() => setActive(i)}
              aria-label={`Show photo ${i + 1}`}
              aria-current={i === active}
            />
          ))}
        </Dots>
      )}
    </CarouselShell>
  );
};

export default Carousel;
